import React, { useEffect, useState } from 'react';
import './App.css';
import { Heart, Users, Search, Gift, Settings, ChevronRight, ArrowLeft, Plus, X, Trash2, ExternalLink, Link as LinkIcon, User, Lock, Unlock, Send, Pencil, Image as ImageIcon, ArrowUpDown, Cake, Palette } from 'lucide-react';

const SkeletonCard = () => (
  <div className="wish-card" style={{ paddingBottom: '16px' }}>
    <div className="skeleton skeleton-title"></div>
    <div className="skeleton skeleton-text"></div>
    <div className="skeleton skeleton-text short"></div>
  </div>
);

function App() {
  const [userName, setUserName] = useState("Пользователь");
  const [currentPage, setCurrentPage] = useState("main");
  const [theme, setTheme] = useState(localStorage.getItem('app-theme') || 'light');
  
  const [wishes, setWishes] = useState([]);
  const [friends, setFriends] = useState([]);
  const [inviteLink, setInviteLink] = useState("");
  const [accessibleLists, setAccessibleLists] = useState([]);
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [friendWishes, setFriendWishes] = useState([]);
  const [sortBy, setSortBy] = useState("priority");

  const [incomingProposals, setIncomingProposals] = useState([]);
  const [outgoingProposals, setOutgoingProposals] = useState([]);
  const [proposalTab, setProposalTab] = useState("incoming"); 
  const [isSuggestModalOpen, setIsSuggestModalOpen] = useState(false);
  const [suggestTitle, setSuggestTitle] = useState("");
  const [suggestPrice, setSuggestPrice] = useState("");
  const [suggestUrl, setSuggestUrl] = useState("");
  const [suggestComment, setSuggestComment] = useState("");

  const [isWishesLoading, setIsWishesLoading] = useState(true);
  const [isFriendWishesLoading, setIsFriendWishesLoading] = useState(false);
  const [isFriendsLoading, setIsFriendsLoading] = useState(true);
  const [isAccessibleListsLoading, setIsAccessibleListsLoading] = useState(true);
  const [isProposalsLoading, setIsProposalsLoading] = useState(true);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingWishId, setEditingWishId] = useState(null); 
  const [newTitle, setNewTitle] = useState("");
  const [newPrice, setNewPrice] = useState("");
  const [newUrl, setNewUrl] = useState("");
  const [newImageUrl, setNewImageUrl] = useState(""); 
  const [newPriority, setNewPriority] = useState("wanted"); 
  const [newComment, setNewComment] = useState("");

  const [birthday, setBirthday] = useState("");
  const [isBirthdaySaving, setIsBirthdaySaving] = useState(false);

  const [bookingWishId, setBookingWishId] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteCountdown, setDeleteCountdown] = useState(3);

  // Все хуки вызваны, теперь можно делать проверки
  const tg = window.Telegram?.WebApp;
  const isInTelegram = !!(tg && tg.initData);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('app-theme', theme);
  }, [theme]);

  useEffect(() => {
    if (!isInTelegram) return;
    tg.ready();
    tg.expand();
    fetch('https://wishesbot.ru/api/auth', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ initData: tg.initData })
    }).then(res => res.json()).then(data => { 
      if (data.status === 'ok') {
        setUserName(data.name);
        loadWishes(true);
        loadFriends(true);
        loadAccessibleLists(true);
        loadProposals(true);
        loadBirthday(true);
      }
    });
  }, [isInTelegram]);

  const handleBounce = (e) => {
    const target = e.currentTarget;
    target.classList.remove('bouncing');
    void target.offsetWidth;
    target.classList.add('bouncing');
    setTimeout(() => target.classList.remove('bouncing'), 300);
  };

  const loadWishes = (silent = false) => {
    if (!silent) setIsWishesLoading(true);
    fetch(`https://wishesbot.ru/api/get_wishes_v3?sort_by=${sortBy}`, { headers: { 'Authorization': `Bearer ${tg?.initData}` } })
    .then(res => res.json()).then(data => setWishes(Array.isArray(data) ? data : []))
    .finally(() => setIsWishesLoading(false));
  };

  const loadFriends = (silent = false) => {
    if (!silent) setIsFriendsLoading(true);
    fetch('https://wishesbot.ru/api/friends', { headers: { 'Authorization': `Bearer ${tg?.initData}` } })
    .then(res => res.json()).then(data => setFriends(Array.isArray(data) ? data : []))
    .finally(() => setIsFriendsLoading(false));
  };

  const loadAccessibleLists = (silent = false) => {
    if (!silent) setIsAccessibleListsLoading(true);
    fetch('https://wishesbot.ru/api/accessible_lists', { headers: { 'Authorization': `Bearer ${tg?.initData}` } })
    .then(res => res.json()).then(data => setAccessibleLists(Array.isArray(data) ? data : []))
    .finally(() => setIsAccessibleListsLoading(false));
  };

  const loadFriendWishes = (friendId, friendName) => {
    setIsFriendWishesLoading(true);
    setSelectedFriend({ id: friendId, name: friendName });
    setCurrentPage("viewing_friend");
    fetch(`https://wishesbot.ru/api/friend_wishes/${friendId}?sort_by=${sortBy}`, { headers: { 'Authorization': `Bearer ${tg?.initData}` } })
    .then(res => res.json()).then(data => { if (Array.isArray(data)) setFriendWishes(data); })
    .finally(() => setIsFriendWishesLoading(false));
  };

  const loadProposals = (silent = false) => {
    if (!silent) setIsProposalsLoading(true);
    const headers = { 'Authorization': `Bearer ${tg?.initData}` };
    Promise.all([
      fetch('https://wishesbot.ru/api/proposals/incoming', { headers }).then(res => res.json()).then(data => setIncomingProposals(Array.isArray(data) ? data : [])),
      fetch('https://wishesbot.ru/api/proposals/outgoing', { headers }).then(res => res.json()).then(data => setOutgoingProposals(Array.isArray(data) ? data : []))
    ]).finally(() => setIsProposalsLoading(false));
  };

  const loadBirthday = (silent = false) => {
    fetch('https://wishesbot.ru/api/get_birthday', { headers: { 'Authorization': `Bearer ${tg?.initData}` } })
    .then(res => res.json())
    .then(data => { if (data.birthday) setBirthday(data.birthday); });
  };

  const saveBirthday = () => {
    setIsBirthdaySaving(true);
    fetch('https://wishesbot.ru/api/set_birthday', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${tg?.initData}` },
      body: JSON.stringify({ birthday: birthday || null })
    }).then(() => { alert("Дата рождения сохранена!"); }).finally(() => setIsBirthdaySaving(false));
  };

  const handleMenuClick = (title) => {
    if (title === "Мои желания") setCurrentPage("wishes");
    else if (title === "Виш-лист Друзей") setCurrentPage("friend_wishlists");
    else if (title === "Мои близкие") setCurrentPage("friends");
    else if (title === "Предложения подарков") setCurrentPage("proposals");
    else if (title === "Настройки") setCurrentPage("settings");
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const compressImage = (file) => new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 800;
          const scaleSize = MAX_WIDTH / img.width;
          canvas.width = MAX_WIDTH;
          canvas.height = img.height * scaleSize;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          canvas.toBlob((blob) => resolve(new File([blob], "compressed.jpg", { type: "image/jpeg" })), 'image/jpeg', 0.7);
        };
      };
    });
    try {
      const compressedFile = await compressImage(file);
      const formData = new FormData();
      formData.append("file", compressedFile);
      const res = await fetch('https://wishesbot.ru/api/upload', { method: 'POST', headers: { 'Authorization': `Bearer ${tg?.initData}` }, body: formData });
      const data = await res.json();
      if (data.image_url) setNewImageUrl(data.image_url);
    } catch (err) { alert("Ошибка загрузки картинки"); }
    e.target.value = null;
  };

  const handleAddWish = (e) => {
    e.preventDefault();
    if (isSaving) return;
    if (!newTitle.trim()) return alert("Название обязательно!");
    setIsSaving(true);
    const wishData = { title: newTitle, price_amount: newPrice ? parseFloat(newPrice) : null, currency: newPrice ? "RUB" : null, url: newUrl || null, comment: newComment || null, image_url: newImageUrl || null, priority: newPriority };
    const url = editingWishId ? `https://wishesbot.ru/api/edit_wish/${editingWishId}` : 'https://wishesbot.ru/api/save_wish_v3';
    const method = editingWishId ? 'PUT' : 'POST';
    fetch(url, { method, headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${tg?.initData}` }, body: JSON.stringify(wishData) })
    .then(() => { setIsFormOpen(false); setNewTitle(""); setNewPrice(""); setNewUrl(""); setNewComment(""); setNewImageUrl(""); setNewPriority("wanted"); setEditingWishId(null); loadWishes(true); })
    .finally(() => setIsSaving(false));
  };

  const handleDeleteWish = (wishId) => {
    if (!window.confirm("Удалить это желание?")) return;
    fetch(`https://wishesbot.ru/api/delete_wish/${wishId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${tg?.initData}` } }).then(() => loadWishes(true));
  };

  const handleBookWish = (isAnonymous) => {
    fetch(`https://wishesbot.ru/api/book_wish/${bookingWishId}`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${tg?.initData}` }, body: JSON.stringify({ is_anonymous: isAnonymous }) })
    .then(res => { if (res.ok) { setBookingWishId(null); loadFriendWishes(selectedFriend.id, selectedFriend.name); } });
  };

  const handleUnbookWish = (wishId) => {
    if (!window.confirm("Отменить бронь?")) return;
    fetch(`https://wishesbot.ru/api/unbook_wish/${wishId}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${tg?.initData}` } }).then(() => loadFriendWishes(selectedFriend.id, selectedFriend.name));
  };

  const handleSuggestGift = (e) => {
    e.preventDefault();
    if (!suggestTitle.trim()) return alert("Название обязательно!");
    fetch('https://wishesbot.ru/api/proposals', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${tg?.initData}` }, body: JSON.stringify({ friend_id: selectedFriend.id, title: suggestTitle, price_amount: suggestPrice ? parseFloat(suggestPrice) : null, currency: suggestPrice ? "RUB" : null, url: suggestUrl || null, comment: suggestComment || null }) })
    .then(() => { setIsSuggestModalOpen(false); setSuggestTitle(""); setSuggestPrice(""); setSuggestUrl(""); setSuggestComment(""); alert("Предложение отправлено!"); });
  };

  const handleAcceptProposal = (id) => {
    fetch(`https://wishesbot.ru/api/proposals/${id}/accept`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${tg?.initData}` }, body: JSON.stringify({ priority: "wanted" }) }).then(() => loadProposals(true));
  };

  const handleRejectProposal = (id) => {
    if (!window.confirm("Отклонить?")) return;
    fetch(`https://wishesbot.ru/api/proposals/${id}/reject`, { method: 'POST', headers: { 'Authorization': `Bearer ${tg?.initData}` } }).then(() => loadProposals(true));
  };

  const handleDeleteProposal = (id) => {
    if (!window.confirm("Удалить это предложение?")) return;
    fetch(`https://wishesbot.ru/api/proposals/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${tg?.initData}` } }).then(() => loadProposals(true));
  };

  const handleDeleteAccount = () => {
    fetch('https://wishesbot.ru/api/delete_account', { method: 'DELETE', headers: { 'Authorization': `Bearer ${tg?.initData}` } })
    .then(res => { if (res.ok) { alert("Аккаунт удален. Закройте приложение."); tg.close(); } else alert("Ошибка при удалении аккаунта."); })
    .catch(err => alert("Сетевая ошибка: " + err.message));
  };

  const handleOpenDeleteModal = () => {
    setIsDeleteModalOpen(true);
    setDeleteCountdown(3);
    const timer = setInterval(() => {
      setDeleteCountdown(prev => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const menuItems = [
    { icon: <Heart color="#ef4444" size={24} />, title: "Мои желания", desc: "Просмотр, добавление и редактирование" },
    { icon: <Search color="#10b981" size={24} />, title: "Виш-лист Друзей", desc: "Просмотр доступных списков" },
    { icon: <Users color="#3b82f6" size={24} />, title: "Мои близкие", desc: "Приглашения, доступ к спискам" },
    { icon: <Gift color="#f59e0b" size={24} />, title: "Предложения подарков", desc: "Входящие предложения и отправленные идеи" },
    { icon: <Settings color="#6b7280" size={24} />, title: "Настройки", desc: "Приватность, уведомления" },
  ];

  const getPriorityBadge = (priority) => {
    switch(priority) {
      case 'very_wanted': return <span style={{ background: '#fee2e2', color: '#ef4444', padding: '4px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: '600' }}>🔥 Очень хочу</span>;
      case 'wanted': return <span style={{ background: '#fef3c7', color: '#d97706', padding: '4px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: '600' }}>⭐ Хочу</span>;
      case 'liked': return <span style={{ background: '#d1fae5', color: '#059669', padding: '4px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: '600' }}>💚 Понравилось</span>;
      case 'needed': return <span style={{ background: '#dbeafe', color: '#2563eb', padding: '4px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: '600' }}>📦 Нужно</span>;
      default: return null;
    }
  };

  const proposedBadge = (
    <span style={{ background: '#e0e7ff', color: '#4f46e5', padding: '4px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: '600' }}>💡 Предложено</span>
  );

  // === ПРОВЕРКА TELEGRAM (после всех хуков) ===
  if (!isInTelegram) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', padding: '20px', textAlign: 'center', fontFamily: 'sans-serif' }}>
        <h2>Ошибка доступа</h2>
        <p style={{ color: '#6b7280', marginBottom: '20px' }}>Пожалуйста, откройте это приложение через Telegram-бота <b>@i_wish_it_bot</b>.</p>
        <a href="https://t.me/i_wish_it_bot" style={{ padding: '12px 24px', background: '#3b82f6', color: 'white', textDecoration: 'none', borderRadius: '12px', fontWeight: 'bold' }}>Открыть бота</a>
      </div>
    );
  }

  // --- ЭКРАН НАСТРОЕК ---
  if (currentPage === "settings") {
    return (
      <div className="app-container">
        <header className="header" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <button onPointerDown={handleBounce} onClick={() => setCurrentPage("main")} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}><ArrowLeft size={24} color="var(--text-main)" /></button>
          <h1 style={{ margin: 0 }}>Настройки</h1>
        </header>
        <div className="wish-card fade-in" style={{ marginBottom: '20px' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}><Palette size={20} color="var(--accent)" /> Оформление</h3>
          <div className="segmented-control">
            <div className="segment-thumb" style={{ transform: `translateX(${theme === 'light' ? 0 : theme === 'dark' ? 100 : 200}%)` }}></div>
            <div className={`segment ${theme === 'light' ? 'active' : ''}`} onPointerDown={handleBounce} onClick={() => setTheme('light')}>Светлая</div>
            <div className={`segment ${theme === 'dark' ? 'active' : ''}`} onPointerDown={handleBounce} onClick={() => setTheme('dark')}>Темная</div>
            <div className={`segment ${theme === 'neon' ? 'active' : ''}`} onPointerDown={handleBounce} onClick={() => setTheme('neon')}>Неон</div>
          </div>
        </div>
        <div className="wish-card fade-in" style={{ marginBottom: '20px' }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '16px' }}>Профиль</h3>
          <p style={{ margin: '0 0 16px 0', color: 'var(--text-muted)', fontSize: '14px' }}>Имя: {userName}</p>
          <div style={{ borderTop: '1px solid var(--card-border)', paddingTop: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: 'var(--text-main)' }}><Cake size={18} color="#ec4899" /> Дата рождения</label>
            <div style={{ display: 'flex', gap: '10px' }}>
              <input type="date" value={birthday} onChange={e => setBirthday(e.target.value)} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--input-bg)', color: 'var(--text-main)', outline: 'none' }} />
              <button onPointerDown={handleBounce} onClick={saveBirthday} disabled={isBirthdaySaving} style={{ background: '#ec4899', color: 'white', border: 'none', padding: '0 16px', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}>{isBirthdaySaving ? "..." : "Сохранить"}</button>
            </div>
          </div>
        </div>
        <div className="fade-in" style={{ background: 'rgba(239, 68, 68, 0.1)', borderRadius: '20px', padding: '16px', border: '1px solid rgba(239, 68, 68, 0.3)', marginBottom: '20px' }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', color: 'var(--danger)' }}>Опасная зона</h3>
          <p style={{ margin: '0 0 16px 0', color: 'var(--danger)', fontSize: '13px' }}>Удаление аккаунта приведет к безвозвратному удалению всех ваших данных.</p>
          <button onPointerDown={handleBounce} onClick={handleOpenDeleteModal} style={{ width: '100%', padding: '12px', background: 'var(--danger)', color: 'white', border: 'none', borderRadius: '12px', fontWeight: '600', cursor: 'pointer' }}>Удалить аккаунт</button>
        </div>
        {isDeleteModalOpen && (
          <div className="modal-overlay">
            <div className="modal-content" style={{ textAlign: 'center' }}>
              <h2 style={{ marginBottom: '10px', color: 'var(--danger)' }}>Вы уверены?</h2>
              <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '20px' }}>Это действие нельзя отменить.</p>
              <button 
                onPointerDown={(e) => deleteCountdown === 0 && handleBounce(e)} 
                onClick={deleteCountdown === 0 ? handleDeleteAccount : null} 
                disabled={deleteCountdown > 0}
                style={{ width: '100%', padding: '14px', background: deleteCountdown > 0 ? '#9ca3af' : 'var(--danger)', color: 'white', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: '600', marginBottom: '10px', cursor: deleteCountdown > 0 ? 'not-allowed' : 'pointer', opacity: deleteCountdown > 0 ? 0.6 : 1 }}
              >
                {deleteCountdown > 0 ? `Подождите ${deleteCountdown}...` : "Да, удалить навсегда"}
              </button>
              <button onPointerDown={handleBounce} onClick={() => { setIsDeleteModalOpen(false); setDeleteCountdown(3); }} style={{ width: '100%', padding: '12px', background: 'transparent', color: 'var(--text-muted)', border: 'none', fontSize: '14px', cursor: 'pointer' }}>Отмена</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // --- ЭКРАН ПРЕДЛОЖЕНИЙ ---
  if (currentPage === "proposals") {
    return (
      <div className="app-container">
        <header className="header" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <button onPointerDown={handleBounce} onClick={() => setCurrentPage("main")} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}><ArrowLeft size={24} color="var(--text-main)" /></button>
          <h1 style={{ margin: 0 }}>Предложения</h1>
        </header>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
          <button onPointerDown={handleBounce} onClick={() => setProposalTab("incoming")} style={{ flex: 1, padding: '10px', borderRadius: '10px', border: 'none', background: proposalTab === "incoming" ? 'var(--accent)' : 'var(--card-bg)', color: proposalTab === "incoming" ? 'white' : 'var(--text-main)', fontWeight: '600', cursor: 'pointer', backdropFilter: 'blur(10px)' }}>Входящие</button>
          <button onPointerDown={handleBounce} onClick={() => setProposalTab("outgoing")} style={{ flex: 1, padding: '10px', borderRadius: '10px', border: 'none', background: proposalTab === "outgoing" ? 'var(--accent)' : 'var(--card-bg)', color: proposalTab === "outgoing" ? 'white' : 'var(--text-main)', fontWeight: '600', cursor: 'pointer', backdropFilter: 'blur(10px)' }}>Отправленные</button>
        </div>
        <div className="wishes-list fade-in">
          {isProposalsLoading ? <><SkeletonCard /><SkeletonCard /></> : proposalTab === "incoming" ? (
            incomingProposals.length === 0 ? <p style={{ color: 'var(--text-muted)', textAlign: 'center', marginTop: '20px' }}>Нет входящих предложений.</p> : (
              incomingProposals.map(p => (
                <div key={p.id} className="wish-card" style={{ position: 'relative', paddingBottom: '50px' }}>
                  <button onPointerDown={handleBounce} onClick={() => handleDeleteProposal(p.id)} style={{ position: 'absolute', top: '16px', right: '16px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={18} /></button>
                  <h3>{p.title}</h3>
                  <p style={{ margin: '4px 0', fontSize: '13px', color: 'var(--text-muted)' }}>От: {p.author_name}</p>
                  {p.price_amount && <p className="price">{p.price_amount} {p.currency}</p>}
                  {p.comment && <p className="comment">{p.comment}</p>}
                  <div style={{ position: 'absolute', bottom: '16px', left: '16px', right: '16px', display: 'flex', gap: '10px', borderTop: '1px solid var(--card-border)', paddingTop: '12px' }}>
                    {p.status === "pending" ? (
                      <><button onPointerDown={handleBounce} onClick={() => handleAcceptProposal(p.id)} style={{ flex: 1, padding: '8px', background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}>Принять</button><button onPointerDown={handleBounce} onClick={() => handleRejectProposal(p.id)} style={{ flex: 1, padding: '8px', background: 'var(--danger)', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}>Отклонить</button></>
                    ) : (
                      <span style={{ fontSize: '14px', fontWeight: '600', color: p.status === "accepted" ? '#10b981' : p.status === "deleted" ? '#6b7280' : 'var(--danger)' }}>{p.status === "accepted" ? "✅ Принято" : p.status === "deleted" ? "🗑️ Удалено из желаний" : "❌ Отклонено"}</span>
                    )}
                  </div>
                </div>
              ))
            )
          ) : (
            outgoingProposals.length === 0 ? <p style={{ color: 'var(--text-muted)', textAlign: 'center', marginTop: '20px' }}>Вы пока не отправляли предложений.</p> : (
              outgoingProposals.map(p => (
                <div key={p.id} className="wish-card" style={{ position: 'relative' }}>
                  <button onPointerDown={handleBounce} onClick={() => handleDeleteProposal(p.id)} style={{ position: 'absolute', top: '16px', right: '16px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={18} /></button>
                  <h3>{p.title}</h3>
                  <p style={{ margin: '4px 0', fontSize: '13px', color: 'var(--text-muted)' }}>Кому: {p.friend_name}</p>
                  {p.price_amount && <p className="price">{p.price_amount} {p.currency}</p>}
                  <div style={{ marginTop: '12px', padding: '8px', background: p.status === 'accepted' ? 'rgba(16, 185, 129, 0.2)' : p.status === 'rejected' ? 'rgba(239, 68, 68, 0.2)' : p.status === 'deleted' ? 'rgba(107, 114, 128, 0.2)' : 'rgba(245, 158, 11, 0.2)', borderRadius: '8px', fontSize: '13px', fontWeight: '600', color: p.status === 'accepted' ? '#10b981' : p.status === 'rejected' ? 'var(--danger)' : p.status === 'deleted' ? '#6b7280' : '#d97706' }}>
                    {p.status === 'pending' ? 'Ожидает решения' : p.status === 'accepted' ? 'Принято владельцем' : p.status === 'deleted' ? 'Удалено владельцем' : 'Отклонено'}
                  </div>
                </div>
              ))
            )
          )}
        </div>
      </div>
    );
  }

  // --- ЭКРАН СПИСКА ДРУГА ---
  if (currentPage === "viewing_friend") {
    return (
      <div className="app-container">
        <header style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
            <button onPointerDown={handleBounce} onClick={() => setCurrentPage("friend_wishlists")} style={{ width: '32px', background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', justifyContent: 'flex-start' }}>
              <ArrowLeft size={24} color="var(--text-main)" />
            </button>
            <h1 style={{ flex: 1, textAlign: 'center', margin: 0, fontSize: '20px', fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Список: {selectedFriend?.name}</h1>
            <div style={{ width: '32px' }}></div>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <button onPointerDown={handleBounce} onClick={() => {
                const newSort = sortBy === "priority" ? "date" : "priority";
                setSortBy(newSort);
                fetch(`https://wishesbot.ru/api/friend_wishes/${selectedFriend.id}?sort_by=${newSort}`, { headers: { 'Authorization': `Bearer ${tg?.initData}` } })
                .then(res => res.json()).then(data => { if (Array.isArray(data)) setFriendWishes(data); });
              }} className="sort-btn">
              <ArrowUpDown size={16} /> {sortBy === "priority" ? "По важности" : "По дате"}
            </button>
            <button onPointerDown={handleBounce} onClick={() => setIsSuggestModalOpen(true)} style={{ background: '#f59e0b', color: 'white', border: 'none', borderRadius: '50%', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 6px rgba(245, 158, 11, 0.4)' }} title="Предложить подарок">
              <Gift size={20} />
            </button>
          </div>
        </header>

        <div className="wishes-list fade-in">
          {isFriendWishesLoading ? (
            <><SkeletonCard /><SkeletonCard /></>
          ) : friendWishes.map(wish => (
            <div key={wish.id} className="wish-card" style={{ position: 'relative', paddingBottom: '60px', opacity: wish.is_booked && !wish.booked_by_me ? 0.6 : 1 }}>
              {wish.image_url && (
                <img src={wish.image_url} alt={wish.title} style={{ width: '100%', height: '160px', objectFit: 'cover', borderRadius: '12px', marginBottom: '12px' }} />
              )}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                {getPriorityBadge(wish.priority)}
                {wish.is_proposed && proposedBadge}
              </div>
              <h3>{wish.title}</h3>
              {wish.price_amount && <p className="price">{wish.price_amount} {wish.currency}</p>}
              {wish.comment && <p className="comment">{wish.comment}</p>}
              {wish.is_booked && (
                <div style={{ marginTop: '10px', padding: '8px', background: wish.booked_by_me ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)', borderRadius: '8px', fontSize: '13px', color: wish.booked_by_me ? '#065f46' : '#991b1b' }}>
                  {wish.booked_by_me ? "🎁 Вы бронируете этот подарок" : `🎁 Забронировано: ${wish.booker_name}`}
                </div>
              )}
              <div style={{ position: 'absolute', bottom: '16px', left: '16px', right: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--card-border)', paddingTop: '12px' }}>
                <div>
                  {wish.url && (
                    <a href={wish.url} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--accent)', textDecoration: 'none', fontSize: '14px', fontWeight: '500' }}><ExternalLink size={16} /> Открыть</a>
                  )}
                </div>
                {!wish.is_booked ? (
                  <button onPointerDown={handleBounce} onClick={() => setBookingWishId(wish.id)} style={{ background: 'var(--accent)', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}>Забронировать</button>
                ) : wish.booked_by_me ? (
                  <button onPointerDown={handleBounce} onClick={() => handleUnbookWish(wish.id)} style={{ background: 'none', color: 'var(--danger)', border: '1px solid var(--danger)', padding: '6px 10px', borderRadius: '8px', cursor: 'pointer', fontWeight: '500', fontSize: '13px' }}>Отменить</button>
                ) : null}
              </div>
            </div>
          ))}
        </div>

        {bookingWishId && (
          <div className="modal-overlay">
            <div className="modal-content" style={{ textAlign: 'center' }}>
              <h2 style={{ marginBottom: '10px' }}>Как забронировать?</h2>
              <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '20px' }}>Владелец списка не узнает о подарке, если вы выберете анонимно.</p>
              <button onPointerDown={handleBounce} onClick={() => handleBookWish(true)} style={{ width: '100%', padding: '14px', background: 'var(--accent)', color: 'white', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: '600', marginBottom: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}><Lock size={18} /> Анонимно</button>
              <button onPointerDown={handleBounce} onClick={() => handleBookWish(false)} style={{ width: '100%', padding: '14px', background: '#10b981', color: 'white', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: '600', marginBottom: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}><Unlock size={18} /> Открыто</button>
              <button onPointerDown={handleBounce} onClick={() => setBookingWishId(null)} style={{ width: '100%', padding: '12px', background: 'transparent', color: 'var(--text-muted)', border: 'none', fontSize: '14px', cursor: 'pointer' }}>Отмена</button>
            </div>
          </div>
        )}

        {isSuggestModalOpen && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h2>Предложить подарок</h2>
                <button onPointerDown={handleBounce} onClick={() => setIsSuggestModalOpen(false)} className="close-btn"><X size={24} /></button>
              </div>
              <form onSubmit={handleSuggestGift} className="wish-form">
                <input type="text" placeholder="Название (обязательно)" value={suggestTitle} onChange={e => setSuggestTitle(e.target.value)} required />
                <input type="number" placeholder="Цена (₽)" value={suggestPrice} onChange={e => setSuggestPrice(e.target.value)} />
                <input type="url" placeholder="Ссылка на товар" value={suggestUrl} onChange={e => setSuggestUrl(e.target.value)} />
                <textarea placeholder="Комментарий" value={suggestComment} onChange={e => setSuggestComment(e.target.value)} rows="2" />
                <button onPointerDown={handleBounce} type="submit" className="submit-btn" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}><Send size={18} /> Отправить</button>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  }

  // --- ЭКРАН СПИСКА ДРУЗЕЙ ---
  if (currentPage === "friend_wishlists") {
    return (
      <div className="app-container">
        <header className="header" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <button onPointerDown={handleBounce} onClick={() => setCurrentPage("main")} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}><ArrowLeft size={24} color="var(--text-main)" /></button>
          <h1 style={{ margin: 0 }}>Виш-листы Друзей</h1>
        </header>
        <div className="wishes-list fade-in">
          {isAccessibleListsLoading ? (
            <><SkeletonCard /><SkeletonCard /></>
          ) : accessibleLists.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', textAlign: 'center', marginTop: '40px' }}>Пока никто не поделился с тобой своим виш-листом.</p>
          ) : (
            accessibleLists.map(friend => (
              <div key={friend.user_id} onPointerDown={handleBounce} onClick={() => loadFriendWishes(friend.user_id, friend.display_name)} className="wish-card" style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div className="menu-icon" style={{ margin: 0, width: '32px', height: '32px' }}><User size={18} color="var(--icon-color)" /></div>
                  <h3 style={{ margin: 0 }}>{friend.display_name}</h3>
                </div>
                <ChevronRight size={20} color="var(--text-muted)" />
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  // --- ЭКРАН МОИХ БЛИЗКИХ ---
  if (currentPage === "friends") {
    return (
      <div className="app-container">
        <header className="header" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <button onPointerDown={handleBounce} onClick={() => setCurrentPage("main")} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}><ArrowLeft size={24} color="var(--text-main)" /></button>
          <h1 style={{ margin: 0 }}>Мои близкие</h1>
        </header>
        <button onPointerDown={handleBounce} className="submit-btn" onClick={() => {
            fetch('https://wishesbot.ru/api/generate_invite', { method: 'POST', headers: { 'Authorization': `Bearer ${tg?.initData}` } })
            .then(res => res.json()).then(data => { setInviteLink(data.invite_link); navigator.clipboard.writeText(data.invite_link).then(() => alert("Ссылка скопирована!")).catch(() => {}); });
          }} style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          <LinkIcon size={20} /> Пригласить друга
        </button>
        {inviteLink && (
          <div className="wish-card fade-in" style={{ marginBottom: '20px', fontSize: '14px', wordBreak: 'break-all' }}>
            <p style={{ margin: '0 0 8px 0', fontWeight: '600' }}>Твоя ссылка:</p>
            <a href={inviteLink} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>{inviteLink}</a>
          </div>
        )}
        <div className="wishes-list fade-in">
          {isFriendsLoading ? (
            <><SkeletonCard /><SkeletonCard /></>
          ) : friends.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', textAlign: 'center', marginTop: '20px' }}>У тебя пока нет близких.</p>
          ) : (
            friends.map(friend => (
              <div key={friend.user_id} className="wish-card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h3 style={{ margin: '0 0 4px 0' }}>{friend.display_name}</h3>
                  <p style={{ margin: 0, fontSize: '13px', color: friend.has_access ? '#10b981' : 'var(--text-muted)' }}>{friend.has_access ? "Видит твой список" : "Нет доступа к списку"}</p>
                </div>
                <button onPointerDown={handleBounce} onClick={() => {
                    fetch('https://wishesbot.ru/api/toggle_access', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${tg?.initData}` }, body: JSON.stringify({ target_user_id: friend.user_id, grant: !friend.has_access }) }).then(() => loadFriends(true));
                  }} style={{ background: friend.has_access ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)', color: friend.has_access ? 'var(--danger)' : '#10b981', border: 'none', padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', fontWeight: '500', fontSize: '13px' }}>
                  {friend.has_access ? "Запретить" : "Разрешить"}
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  // --- ЭКРАН МОИХ ЖЕЛАНИЙ ---
  if (currentPage === "wishes") {
    return (
      <div className="app-container">
        <header style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
            <button onPointerDown={handleBounce} onClick={() => setCurrentPage("main")} style={{ width: '32px', background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', justifyContent: 'flex-start' }}>
              <ArrowLeft size={24} color="var(--text-main)" />
            </button>
            <h1 style={{ flex: 1, textAlign: 'center', margin: 0, fontSize: '20px', fontWeight: 'bold' }}>Мои желания</h1>
            <div style={{ width: '32px' }}></div>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <button onPointerDown={handleBounce} onClick={() => {
                const newSort = sortBy === "priority" ? "date" : "priority";
                setSortBy(newSort);
                fetch(`https://wishesbot.ru/api/get_wishes_v3?sort_by=${newSort}`, { headers: { 'Authorization': `Bearer ${tg?.initData}` } })
                .then(res => res.json()).then(data => setWishes(Array.isArray(data) ? data : []));
              }} className="sort-btn">
              <ArrowUpDown size={16} /> {sortBy === "priority" ? "По важности" : "По дате"}
            </button>
            <button onPointerDown={handleBounce} onClick={() => {
              setEditingWishId(null); setNewTitle(""); setNewPrice(""); setNewUrl(""); setNewImageUrl(""); setNewComment(""); setNewPriority("wanted");
              setIsFormOpen(true);
            }} style={{ background: 'var(--accent)', color: 'white', border: 'none', borderRadius: '50%', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.2)' }}>
              <Plus size={24} />
            </button>
          </div>
        </header>

        <div className="wishes-list fade-in">
          {isWishesLoading ? (
            <><SkeletonCard /><SkeletonCard /><SkeletonCard /></>
          ) : wishes.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', textAlign: 'center', marginTop: '40px' }}>У тебя пока нет желаний.</p>
          ) : (
            wishes.map(wish => (
              <div key={wish.id} className="wish-card" style={{ position: 'relative', paddingBottom: '50px' }}>
                {wish.image_url && (
                  <img src={wish.image_url} alt={wish.title} style={{ width: '100%', height: '160px', objectFit: 'cover', borderRadius: '12px', marginBottom: '12px' }} />
                )}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                  {getPriorityBadge(wish.priority)}
                  {wish.is_proposed && proposedBadge}
                </div>
                <h3>{wish.title}</h3>
                {wish.price_amount && <p className="price">{wish.price_amount} {wish.currency}</p>}
                {wish.comment && <p className="comment">{wish.comment}</p>}
                {wish.is_booked && (
                  <div style={{ marginTop: '10px', padding: '8px', background: 'rgba(239, 68, 68, 0.2)', borderRadius: '8px', fontSize: '13px', color: '#991b1b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Lock size={14} /> {wish.booker_name ? `Забронировано: ${wish.booker_name}` : "Забронировано (анонимно)"}
                  </div>
                )}
                <div style={{ position: 'absolute', bottom: '16px', left: '16px', right: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--card-border)', paddingTop: '12px' }}>
                  <div>
                    {wish.url && (
                      <a href={wish.url} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--accent)', textDecoration: 'none', fontSize: '14px', fontWeight: '500' }}><ExternalLink size={16} /> Открыть</a>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button onPointerDown={handleBounce} onClick={() => {
                      setEditingWishId(wish.id);
                      setNewTitle(wish.title); setNewPrice(wish.price_amount || ""); setNewUrl(wish.url || ""); 
                      setNewImageUrl(wish.image_url || ""); setNewComment(wish.comment || ""); setNewPriority(wish.priority);
                      setIsFormOpen(true);
                    }} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '14px', fontWeight: '500', padding: 0 }}>
                      <Pencil size={16} /> Изменить
                    </button>
                    <button onPointerDown={handleBounce} onClick={() => handleDeleteWish(wish.id)} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: '14px', fontWeight: '500', padding: 0 }}><Trash2 size={16} /> Удалить</button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {isFormOpen && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h2>{editingWishId ? "Редактировать желание" : "Новое желание"}</h2>
                <button onPointerDown={handleBounce} onClick={() => { setIsFormOpen(false); setEditingWishId(null); }} className="close-btn"><X size={24} /></button>
              </div>
              <form onSubmit={handleAddWish} className="wish-form">
                <input type="text" placeholder="Название (обязательно)" value={newTitle} onChange={e => setNewTitle(e.target.value)} required />
                <select value={newPriority} onChange={e => setNewPriority(e.target.value)} style={{ width: '100%', padding: '12px 16px', borderRadius: '12px', border: '1px solid var(--input-border)', background: 'var(--input-bg)', color: 'var(--text-main)', fontSize: '15px', outline: 'none' }}>
                  <option value="very_wanted">🔥 Очень хочу</option>
                  <option value="wanted">⭐ Хочу</option>
                  <option value="liked">💚 Понравилось</option>
                  <option value="needed">📦 Нужно</option>
                </select>
                <input type="number" placeholder="Цена (₽)" value={newPrice} onChange={e => setNewPrice(e.target.value)} />
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input type="url" placeholder="Ссылка на товар" value={newUrl} onChange={e => setNewUrl(e.target.value)} style={{ flex: 1 }} />
                  <button onPointerDown={handleBounce} type="button" onClick={async () => {
                      if (!newUrl) return alert("Вставьте ссылку");
                      setIsSaving(true);
                      try {
                        const res = await fetch('https://wishesbot.ru/api/parse_url', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ url: newUrl }) });
                        if (!res.ok) throw new Error("Не удалось распарсить");
                        const data = await res.json();
                        if (data.title) setNewTitle(data.title);
                        if (data.description) setNewComment(data.description);
                        if (data.price_amount) setNewPrice(data.price_amount);
                        if (data.image_url) setNewImageUrl(data.image_url);
                        alert("Данные заполнены! Проверьте и сохраните.");
                      } catch (err) { alert("Ошибка: " + err.message); } finally { setIsSaving(false); }
                    }} disabled={isSaving} style={{ background: '#8b5cf6', color: 'white', border: 'none', padding: '0 12px', borderRadius: '12px', cursor: 'pointer', fontWeight: '600', fontSize: '13px', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    ✨ AI
                  </button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '10px', background: 'rgba(0,0,0,0.05)', borderRadius: '12px', border: '1px dashed var(--input-border)' }}>
                  <label style={{ fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: 'var(--accent)' }}>
                    <ImageIcon size={18} /> Загрузить с устройства
                    <input type="file" accept="image/*" onChange={handleFileUpload} style={{ display: 'none' }} />
                  </label>
                  <span style={{ fontSize: '12px', color: 'var(--text-muted)', textAlign: 'center' }}>или вставьте ссылку</span>
                  <input type="url" placeholder="URL картинки" value={newImageUrl} onChange={e => setNewImageUrl(e.target.value)} style={{ padding: '8px' }} />
                  {newImageUrl && <img src={newImageUrl} alt="Превью" style={{ width: '100%', height: '100px', objectFit: 'cover', borderRadius: '8px', marginTop: '4px' }} />}
                </div>
                <textarea placeholder="Комментарий" value={newComment} onChange={e => setNewComment(e.target.value)} rows="2" />
                <button onPointerDown={handleBounce} type="submit" className="submit-btn" disabled={isSaving} style={{ opacity: isSaving ? 0.7 : 1 }}>
                  {isSaving ? "Сохранение..." : (editingWishId ? "Сохранить изменения" : "Добавить")}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  }

  // --- ГЛАВНЫЙ ЭКРАН ---
  return (
    <div className="app-container">
      <header className="header">
        <h1>🎁 Виш-лист</h1>
        <p>Привет, {userName}! Что хочешь сделать?</p>
      </header>
      <main className="menu-list">
        {menuItems.map((item, index) => (
          <div key={index} className="menu-card" onPointerDown={handleBounce} onClick={() => handleMenuClick(item.title)}>
            <div className="menu-icon">{item.icon}</div>
            <div className="menu-content">
              <h3>{item.title}</h3>
              <p>{item.desc}</p>
            </div>
            <ChevronRight color="var(--text-muted)" size={20} />
          </div>
        ))}
      </main>
    </div>
  );
}

export default App;
